import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { AppLayout, GlassCard, PageHeader, LoadingSpinner } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Slider } from '../components/ui/slider';
import OBBBADisclaimer from '../components/OBBBADisclaimer';
import FederalAidMeter from '../components/FederalAidMeter';
import WorkforcePellCallout from '../components/WorkforcePellCallout';
import { 
  DollarSign, TrendingUp, PiggyBank, Calculator, Target, AlertTriangle,
  GraduationCap, Wrench, Shield, Rocket, Briefcase, Award, BookOpen,
  ChevronRight, CheckCircle, Clock, Lightbulb, BarChart3, ArrowRight,
  Percent, Calendar, ExternalLink, Download, Zap, Users, Home, CreditCard,
  Building, Landmark, HandCoins, BadgeDollarSign, FileText, Star
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Path cost data
const PATH_COSTS = {
  college: {
    name: 'College',
    icon: GraduationCap,
    color: 'blue',
    gradient: 'from-blue-600 to-cyan-600',
    avgCost: 180000,
    timeToIncome: '4-5 years',
    debtRisk: 'High',
    breakEven: '8-12 years',
    monthlyCost: 3750,
    details: {
      tuition: { label: 'Tuition & Fees', avg: 120000, range: '80k-200k' },
      room: { label: 'Room & Board', avg: 48000, range: '40k-60k' },
      books: { label: 'Books & Supplies', avg: 4800, range: '4k-6k' },
      other: { label: 'Other Expenses', avg: 7200, range: '6k-10k' }
    }
  },
  trade: {
    name: 'Skilled Trade',
    icon: Wrench,
    color: 'amber',
    gradient: 'from-amber-600 to-orange-600',
    avgCost: 12000,
    timeToIncome: '6-24 months',
    debtRisk: 'Low',
    breakEven: '1-2 years',
    monthlyCost: 1000,
    details: {
      program: { label: 'Training Program', avg: 8000, range: '3k-15k' },
      tools: { label: 'Tools & Equipment', avg: 2000, range: '1k-5k' },
      certification: { label: 'Certifications', avg: 1500, range: '500-3k' },
      other: { label: 'Other', avg: 500, range: '0-1k' }
    }
  },
  military: {
    name: 'Military',
    icon: Shield,
    color: 'emerald',
    gradient: 'from-emerald-600 to-green-600',
    avgCost: 0,
    timeToIncome: 'Immediate',
    debtRisk: 'None',
    breakEven: 'Immediate',
    monthlyCost: 0,
    details: {
      training: { label: 'Training', avg: 0, range: 'Paid' },
      housing: { label: 'Housing', avg: 0, range: 'Provided' },
      healthcare: { label: 'Healthcare', avg: 0, range: 'Provided' },
      giBill: { label: 'GI Bill Value', avg: -200000, range: '$200k+' }
    }
  },
  entrepreneurship: {
    name: 'Entrepreneurship',
    icon: Rocket,
    color: 'purple',
    gradient: 'from-purple-600 to-pink-600',
    avgCost: 5000,
    timeToIncome: '1-3 years',
    debtRisk: 'Medium',
    breakEven: '2-5 years',
    monthlyCost: 400,
    details: {
      learning: { label: 'Courses & Learning', avg: 2000, range: '0-5k' },
      startup: { label: 'Startup Costs', avg: 2000, range: '500-10k' },
      tools: { label: 'Tools & Software', avg: 600, range: '0-2k' },
      marketing: { label: 'Marketing', avg: 400, range: '0-2k' }
    }
  },
  workforce: {
    name: 'Direct Workforce',
    icon: Briefcase,
    color: 'rose',
    gradient: 'from-rose-600 to-red-600',
    avgCost: 500,
    timeToIncome: 'Immediate',
    debtRisk: 'None',
    breakEven: 'Immediate',
    monthlyCost: 50,
    details: {
      certifications: { label: 'Certifications', avg: 300, range: '0-1k' },
      attire: { label: 'Professional Attire', avg: 150, range: '100-300' },
      transport: { label: 'Initial Transport', avg: 50, range: '0-200' },
      other: { label: 'Other', avg: 0, range: '0' }
    }
  }
};

// Financial Resources Database
const FINANCIAL_RESOURCES = {
  savings: [
    { name: '529 College Savings Plan', description: 'Tax-advantaged education savings', icon: PiggyBank, link: 'https://www.sec.gov/529-plans', type: 'savings' },
    { name: 'Coverdell ESA', description: 'Education Savings Account with $2k/year limit', icon: Landmark, link: 'https://www.irs.gov/taxtopics/tc310', type: 'savings' },
    { name: 'Custodial Accounts (UTMA/UGMA)', description: 'Flexible investment accounts for minors', icon: Building, link: '#', type: 'savings' },
    { name: 'High-Yield Savings', description: 'Simple savings with 4-5% APY', icon: DollarSign, link: '#', type: 'savings' },
  ],
  loans: [
    { name: 'Federal Direct Subsidized', description: 'Need-based, no interest while in school', icon: Landmark, rate: '5.50%', type: 'loan' },
    { name: 'Federal Direct Unsubsidized', description: 'Available to all students', icon: Landmark, rate: '6.50%', type: 'loan' },
    { name: 'Parent PLUS Loans', description: 'Federal loans for parents', icon: Users, rate: '8.05%', type: 'loan' },
    { name: 'Private Student Loans', description: 'Bank/credit union options', icon: Building, rate: '4-14%', type: 'loan' },
  ],
  aid: [
    { name: 'FAFSA', description: 'Federal Student Aid Application - Required first step', icon: FileText, deadline: 'Oct 1 - Jun 30', type: 'aid' },
    { name: 'CSS Profile', description: 'Additional aid form for private colleges', icon: FileText, deadline: 'Varies by school', type: 'aid' },
    { name: 'Pell Grant', description: 'Up to $7,395/year for need-based aid', icon: Award, amount: '$7,395', type: 'aid' },
    { name: 'State Aid Programs', description: 'State-specific grants and aid', icon: Landmark, amount: 'Varies', type: 'aid' },
  ],
  scholarships: [
    { name: 'Merit-Based Scholarships', description: 'Academic achievement awards', icon: Star, type: 'scholarship' },
    { name: 'Athletic Scholarships', description: 'NCAA D1/D2 opportunities', icon: Award, type: 'scholarship' },
    { name: 'Need-Based Scholarships', description: 'Based on financial situation', icon: HandCoins, type: 'scholarship' },
    { name: 'Local Community Scholarships', description: 'Rotary, Lions, businesses', icon: Building, type: 'scholarship' },
  ]
};

// Budget Calculator Component
const BudgetCalculator = ({ onNeedMoreIncome }) => {
  const [budget, setBudget] = useState({
    currentSavings: 0,
    monthlyContribution: 0,
    yearsUntilNeeded: 4,
    expectedAid: 0,
    targetPath: 'college'
  });
  const [results, setResults] = useState(null);

  const calculateProjection = () => {
    const pathCost = PATH_COSTS[budget.targetPath].avgCost;
    const totalSaved = budget.currentSavings + (budget.monthlyContribution * 12 * budget.yearsUntilNeeded);
    const totalWithAid = totalSaved + budget.expectedAid;
    const gap = Math.max(0, pathCost - totalWithAid);
    const monthlyNeeded = gap > 0 ? Math.ceil(gap / (budget.yearsUntilNeeded * 12)) : 0;
    const fundedPercent = Math.min(100, Math.round((totalWithAid / pathCost) * 100));
    
    setResults({
      pathCost,
      totalSaved,
      totalWithAid,
      gap,
      monthlyNeeded,
      fundedPercent,
      surplusOrDeficit: totalWithAid - pathCost
    });
  };

  useEffect(() => {
    calculateProjection();
  }, [budget]);

  const PathIcon = PATH_COSTS[budget.targetPath].icon;

  return (
    <div className="space-y-6">
      {/* Path Selector */}
      <div>
        <Label className="text-slate-300 mb-3 block">What path are you planning for?</Label>
        <div className="grid grid-cols-5 gap-2">
          {Object.entries(PATH_COSTS).map(([key, path]) => {
            const Icon = path.icon;
            return (
              <button
                key={key}
                onClick={() => setBudget(prev => ({ ...prev, targetPath: key }))}
                className={`p-3 rounded-xl border-2 transition-all ${
                  budget.targetPath === key
                    ? `border-${path.color}-500 bg-${path.color}-500/20`
                    : 'border-slate-700 bg-slate-800/50 hover:border-slate-600'
                }`}
              >
                <Icon className={`w-5 h-5 mx-auto mb-1 ${budget.targetPath === key ? `text-${path.color}-400` : 'text-slate-300'}`} />
                <p className={`text-xs ${budget.targetPath === key ? 'text-white' : 'text-slate-300'}`}>{path.name}</p>
              </button>
            );
          })}
        </div>
      </div>

      {/* Input Fields */}
      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <Label className="text-slate-300">Current Savings</Label>
          <div className="relative mt-2">
            <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-300" />
            <Input
              type="number"
              value={budget.currentSavings}
              onChange={(e) => setBudget(prev => ({ ...prev, currentSavings: parseInt(e.target.value) || 0 }))}
              className="pl-9 bg-slate-800 border-slate-700 text-white"
              placeholder="0"
            />
          </div>
        </div>

        <div>
          <Label className="text-slate-300">Monthly Contribution</Label>
          <div className="relative mt-2">
            <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-300" />
            <Input
              type="number"
              value={budget.monthlyContribution}
              onChange={(e) => setBudget(prev => ({ ...prev, monthlyContribution: parseInt(e.target.value) || 0 }))}
              className="pl-9 bg-slate-800 border-slate-700 text-white"
              placeholder="0"
            />
          </div>
        </div>

        <div>
          <Label className="text-slate-300">Years Until Needed</Label>
          <div className="flex items-center gap-4 mt-2">
            <Slider
              value={[budget.yearsUntilNeeded]}
              onValueChange={([val]) => setBudget(prev => ({ ...prev, yearsUntilNeeded: val }))}
              max={10}
              min={1}
              step={1}
              className="flex-1"
            />
            <span className="text-white font-medium w-16">{budget.yearsUntilNeeded} years</span>
          </div>
        </div>

        <div>
          <Label className="text-slate-300">Expected Aid/Scholarships</Label>
          <div className="relative mt-2">
            <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-300" />
            <Input
              type="number"
              value={budget.expectedAid}
              onChange={(e) => setBudget(prev => ({ ...prev, expectedAid: parseInt(e.target.value) || 0 }))}
              className="pl-9 bg-slate-800 border-slate-700 text-white"
              placeholder="0"
            />
          </div>
        </div>
      </div>

      {/* Results */}
      {results && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-6"
        >
          {/* Progress Bar */}
          <div className={`p-6 rounded-2xl bg-gradient-to-r ${PATH_COSTS[budget.targetPath].gradient}`}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <PathIcon className="w-8 h-8 text-white" />
                <div>
                  <p className="text-white/80 text-sm">Funding Progress</p>
                  <p className="text-2xl font-bold text-white">{results.fundedPercent}% Funded</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-white/80 text-sm">Target Cost</p>
                <p className="text-2xl font-bold text-white">${results.pathCost.toLocaleString()}</p>
              </div>
            </div>
            <div className="h-4 bg-white/20 rounded-full overflow-hidden">
              <motion.div 
                className="h-full bg-white rounded-full"
                initial={{ width: 0 }}
                animate={{ width: `${results.fundedPercent}%` }}
                transition={{ duration: 0.8 }}
              />
            </div>
          </div>

          {/* Stats Grid */}
          <div className="grid md:grid-cols-4 gap-4">
            <GlassCard className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <PiggyBank className="w-4 h-4 text-emerald-400" />
                <span className="text-slate-300 text-sm">You'll Save</span>
              </div>
              <p className="text-xl font-bold text-white">${results.totalSaved.toLocaleString()}</p>
            </GlassCard>

            <GlassCard className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <Award className="w-4 h-4 text-blue-400" />
                <span className="text-slate-300 text-sm">With Aid</span>
              </div>
              <p className="text-xl font-bold text-white">${results.totalWithAid.toLocaleString()}</p>
            </GlassCard>

            <GlassCard className="p-4">
              <div className="flex items-center gap-2 mb-2">
                {results.gap > 0 ? (
                  <AlertTriangle className="w-4 h-4 text-yellow-400" />
                ) : (
                  <CheckCircle className="w-4 h-4 text-emerald-400" />
                )}
                <span className="text-slate-300 text-sm">Gap</span>
              </div>
              <p className={`text-xl font-bold ${results.gap > 0 ? 'text-yellow-400' : 'text-emerald-400'}`}>
                {results.gap > 0 ? `-$${results.gap.toLocaleString()}` : 'Fully Funded!'}
              </p>
            </GlassCard>

            <GlassCard className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="w-4 h-4 text-purple-400" />
                <span className="text-slate-300 text-sm">Need/Month</span>
              </div>
              <p className="text-xl font-bold text-white">
                {results.monthlyNeeded > 0 ? `+$${results.monthlyNeeded.toLocaleString()}` : '$0'}
              </p>
            </GlassCard>
          </div>

          {/* Gap Alert & Upsell */}
          {results.gap > 0 && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="p-6 rounded-2xl bg-gradient-to-r from-purple-900/50 to-pink-900/50 border border-purple-500/30"
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Lightbulb className="w-6 h-6 text-purple-400" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-white mb-2">Need to Close the Gap?</h3>
                  <p className="text-slate-300 mb-4">
                    To fully fund this path, you need an additional <span className="text-purple-400 font-semibold">${results.monthlyNeeded}/month</span>.
                    FuturePath's Career Professional platform can help you explore income-boosting opportunities.
                  </p>
                  <div className="flex items-center gap-3">
                    <Button 
                      onClick={onNeedMoreIncome}
                      className="bg-purple-600 hover:bg-purple-700"
                    >
                      <Rocket className="w-4 h-4 mr-2" />
                      Explore Career Options
                    </Button>
                    <Button variant="outline" className="border-purple-500/50 text-purple-300">
                      <Calculator className="w-4 h-4 mr-2" />
                      Side Income Ideas
                    </Button>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {budget.targetPath === 'college' && (
            <FederalAidMeter
              totalCost={results.pathCost}
              annualCost={results.pathCost / (budget.yearsUntilNeeded || 4)}
              years={budget.yearsUntilNeeded || 4}
              borrowerType="undergrad"
              dependencyStatus="dependent"
              yearInProgram={1}
              estimatedAnnualAid={(budget.expectedAid || 0) / (budget.yearsUntilNeeded || 4)}
            />
          )}
        </motion.div>
      )}
    </div>
  );
};
const PathComparisonTool = () => {
  const [selectedPaths, setSelectedPaths] = useState(['college', 'trade']);

  const togglePath = (pathId) => {
    if (selectedPaths.includes(pathId)) {
      if (selectedPaths.length > 1) {
        setSelectedPaths(prev => prev.filter(p => p !== pathId));
      }
    } else {
      setSelectedPaths(prev => [...prev, pathId]);
    }
  };

  return (
    <div className="space-y-6">
      {/* Path Selector */}
      <div className="flex flex-wrap gap-2">
        {Object.entries(PATH_COSTS).map(([key, path]) => {
          const Icon = path.icon;
          const isSelected = selectedPaths.includes(key);
          return (
            <button
              key={key}
              onClick={() => togglePath(key)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 transition-all ${
                isSelected
                  ? `border-${path.color}-500 bg-${path.color}-500/20 text-white`
                  : 'border-slate-700 bg-slate-800/50 text-slate-300 hover:border-slate-600'
              }`}
            >
              <Icon className="w-4 h-4" />
              {path.name}
              {isSelected && <CheckCircle className="w-4 h-4 text-emerald-400" />}
            </button>
          );
        })}
      </div>

      {/* Comparison Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-slate-700">
              <th className="text-left py-3 px-4 text-slate-300 font-medium">Metric</th>
              {selectedPaths.map(pathId => {
                const path = PATH_COSTS[pathId];
                const Icon = path.icon;
                return (
                  <th key={pathId} className="text-center py-3 px-4">
                    <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-lg bg-${path.color}-500/20`}>
                      <Icon className={`w-4 h-4 text-${path.color}-400`} />
                      <span className="text-white font-medium">{path.name}</span>
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            <tr className="border-b border-slate-800">
              <td className="py-4 px-4 text-slate-300">Total Cost</td>
              {selectedPaths.map(pathId => (
                <td key={pathId} className="py-4 px-4 text-center">
                  <span className="text-xl font-bold text-white">
                    ${PATH_COSTS[pathId].avgCost.toLocaleString()}
                  </span>
                </td>
              ))}
            </tr>
            <tr className="border-b border-slate-800">
              <td className="py-4 px-4 text-slate-300">Time to Income</td>
              {selectedPaths.map(pathId => (
                <td key={pathId} className="py-4 px-4 text-center text-white">
                  {PATH_COSTS[pathId].timeToIncome}
                </td>
              ))}
            </tr>
            <tr className="border-b border-slate-800">
              <td className="py-4 px-4 text-slate-300">Debt Risk</td>
              {selectedPaths.map(pathId => {
                const risk = PATH_COSTS[pathId].debtRisk;
                const color = risk === 'High' ? 'red' : risk === 'Medium' ? 'yellow' : 'emerald';
                return (
                  <td key={pathId} className="py-4 px-4 text-center">
                    <span className={`px-3 py-1 rounded-full text-sm bg-${color}-500/20 text-${color}-400`}>
                      {risk}
                    </span>
                  </td>
                );
              })}
            </tr>
            <tr className="border-b border-slate-800">
              <td className="py-4 px-4 text-slate-300">Break-Even</td>
              {selectedPaths.map(pathId => (
                <td key={pathId} className="py-4 px-4 text-center text-white">
                  {PATH_COSTS[pathId].breakEven}
                </td>
              ))}
            </tr>
            <tr>
              <td className="py-4 px-4 text-slate-300">Monthly Investment</td>
              {selectedPaths.map(pathId => (
                <td key={pathId} className="py-4 px-4 text-center">
                  <span className="text-lg font-semibold text-white">
                    ${PATH_COSTS[pathId].monthlyCost.toLocaleString()}/mo
                  </span>
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>

      {/* ROI Chart Placeholder */}
      <GlassCard className="p-6">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-blue-400" />
          10-Year ROI Comparison
        </h3>
        <div className="space-y-4">
          {selectedPaths.map(pathId => {
            const path = PATH_COSTS[pathId];
            // Simulated ROI calculation
            const roi = pathId === 'college' ? 180 : pathId === 'trade' ? 320 : pathId === 'military' ? 500 : pathId === 'entrepreneurship' ? 250 : 150;
            return (
              <div key={pathId}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-slate-300">{path.name}</span>
                  <span className={`text-${path.color}-400 font-semibold`}>{roi}% ROI</span>
                </div>
                <div className="h-3 bg-slate-800 rounded-full overflow-hidden">
                  <motion.div
                    className={`h-full bg-gradient-to-r ${path.gradient}`}
                    initial={{ width: 0 }}
                    animate={{ width: `${Math.min(roi / 5, 100)}%` }}
                    transition={{ duration: 0.8, delay: 0.2 }}
                  />
                </div>
              </div>
            );
          })}
        </div>
        <p className="text-xs text-slate-300 mt-4">* ROI calculated based on average earnings minus costs over 10 years</p>
      </GlassCard>
    </div>
  );
};

// Financial Resources Panel
const FinancialResourcesPanel = () => {
  const [activeCategory, setActiveCategory] = useState('savings');

  const categories = [
    { id: 'savings', name: 'Savings Options', icon: PiggyBank, color: 'emerald' },
    { id: 'loans', name: 'Loan Options', icon: Landmark, color: 'blue' },
    { id: 'aid', name: 'Financial Aid', icon: Award, color: 'purple' },
    { id: 'scholarships', name: 'Scholarships', icon: Star, color: 'yellow' },
  ];

  return (
    <div className="space-y-6">
      {/* Category Tabs */}
      <div className="flex flex-wrap gap-2">
        {categories.map(cat => {
          const Icon = cat.icon;
          return (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                activeCategory === cat.id
                  ? `bg-${cat.color}-500/20 text-${cat.color}-400 border border-${cat.color}-500/50`
                  : 'bg-slate-800/50 text-slate-300 border border-slate-700 hover:border-slate-600'
              }`}
            >
              <Icon className="w-4 h-4" />
              {cat.name}
            </button>
          );
        })}
      </div>

      {/* Resources Grid */}
      <div className="grid md:grid-cols-2 gap-4">
        {FINANCIAL_RESOURCES[activeCategory]?.map((resource, idx) => {
          const Icon = resource.icon;
          const cat = categories.find(c => c.id === activeCategory);
          return (
            <GlassCard key={idx} className="p-5 hover:border-blue-500/30 transition-all">
              <div className="flex items-start gap-4">
                <div className={`w-12 h-12 rounded-xl bg-${cat.color}-500/20 flex items-center justify-center flex-shrink-0`}>
                  <Icon className={`w-6 h-6 text-${cat.color}-400`} />
                </div>
                <div className="flex-1">
                  <h4 className="text-white font-semibold mb-1">{resource.name}</h4>
                  <p className="text-slate-300 text-sm mb-3">{resource.description}</p>
                  <div className="flex items-center gap-4 text-sm">
                    {resource.rate && (
                      <span className="text-blue-400">Rate: {resource.rate}</span>
                    )}
                    {resource.amount && (
                      <span className="text-emerald-400">Up to {resource.amount}</span>
                    )}
                    {resource.deadline && (
                      <span className="text-yellow-400">{resource.deadline}</span>
                    )}
                    {resource.link && resource.link !== '#' && (
                      <a href={resource.link} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 flex items-center gap-1">
                        Learn More <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </GlassCard>
          );
        })}
      </div>

      {/* Pro Tip */}
      <div className="p-4 rounded-xl bg-blue-500/10 border border-blue-500/30">
        <div className="flex items-start gap-3">
          <Lightbulb className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="text-white font-medium mb-1">Pro Tip</h4>
            <p className="text-slate-300 text-sm">
              {activeCategory === 'savings' && "529 plans offer tax-free growth AND many states offer tax deductions for contributions."}
              {activeCategory === 'loans' && "Always exhaust federal loan options before considering private loans - better rates and protections."}
              {activeCategory === 'aid' && "File FAFSA as early as possible (Oct 1) - some aid is first-come, first-served."}
              {activeCategory === 'scholarships' && "Local scholarships have fewer applicants - check Rotary, Lions Club, and local businesses."}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Scholarship Tracker Component
const ScholarshipTracker = () => {
  const scholarships = [
    { name: 'Gates Scholarship', amount: '$0-Full Ride', deadline: '2025-09-15', status: 'open', match: 92 },
    { name: 'Dell Scholars Program', amount: '$20,000', deadline: '2025-12-01', status: 'open', match: 85 },
    { name: 'Coca-Cola Scholars', amount: '$20,000', deadline: '2025-10-31', status: 'open', match: 78 },
    { name: 'Jack Kent Cooke', amount: '$55,000/year', deadline: '2025-11-18', status: 'open', match: 72 },
    { name: 'QuestBridge', amount: 'Full Ride', deadline: '2025-09-26', status: 'open', match: 88 },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-white">AI-Matched Scholarships</h3>
        <Button size="sm" className="bg-blue-600 hover:bg-blue-700">
          <Target className="w-4 h-4 mr-2" />
          Find More Matches
        </Button>
      </div>

      <div className="space-y-3">
        {scholarships.map((scholarship, idx) => (
          <GlassCard key={idx} className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg bg-yellow-500/20 flex items-center justify-center">
                  <Award className="w-5 h-5 text-yellow-400" />
                </div>
                <div>
                  <h4 className="text-white font-medium">{scholarship.name}</h4>
                  <div className="flex items-center gap-3 text-sm">
                    <span className="text-emerald-400">{scholarship.amount}</span>
                    <span className="text-slate-300">•</span>
                    <span className="text-slate-300">Due: {new Date(scholarship.deadline).toLocaleDateString()}</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-right">
                  <p className="text-xs text-slate-300">Match Score</p>
                  <p className={`text-lg font-bold ${scholarship.match >= 80 ? 'text-emerald-400' : 'text-yellow-400'}`}>
                    {scholarship.match}%
                  </p>
                </div>
                <Button size="sm" variant="outline" className="border-slate-600">
                  Apply <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
          </GlassCard>
        ))}
      </div>

      <div className="p-4 rounded-xl bg-purple-500/10 border border-purple-500/30">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Zap className="w-5 h-5 text-purple-400" />
            <div>
              <p className="text-white font-medium">Unlock More Scholarships</p>
              <p className="text-slate-300 text-sm">Premium users get access to 10,000+ curated scholarships</p>
            </div>
          </div>
          <Button className="bg-purple-600 hover:bg-purple-700">
            Upgrade
          </Button>
        </div>
      </div>
    </div>
  );
};

// Main Financial Toolkit Page
export default function ParentFinancialToolkit() {
  const { user, getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('budget');
  const [loading, setLoading] = useState(false);

  const handleNeedMoreIncome = () => {
    // This would trigger the upsell flow to Career Professional
    toast.info('Redirecting to Career Professional options...');
    navigate('/app/calculator'); // Redirect to lifestyle calculator as interim
  };

  return (
    <AppLayout>
      <PageHeader 
        title="Financial Planning Toolkit"
        description="Strategic tools to plan and fund your child's future path"
      />

      {/* OBBBA notice, July 1, 2026 federal student loan rule changes */}
      <OBBBADisclaimer dismissible />
      <div className="mb-6"><WorkforcePellCallout /></div>

      {/* Financial Disclaimer */}
      <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/20 mb-6">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-slate-300 text-sm">
              <strong className="text-amber-400">Disclaimer:</strong> This tool provides estimates for educational purposes only. 
              It does not constitute financial advice. Please consult with a qualified financial advisor before making any 
              financial decisions regarding education funding.
            </p>
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <GlassCard className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-500/20 rounded-lg flex items-center justify-center">
              <PiggyBank className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <p className="text-xs text-slate-300">Avg College Cost</p>
              <p className="text-lg font-bold text-white">$180,000</p>
            </div>
          </div>
        </GlassCard>

        <GlassCard className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center">
              <Wrench className="w-5 h-5 text-amber-400" />
            </div>
            <div>
              <p className="text-xs text-slate-300">Avg Trade Cost</p>
              <p className="text-lg font-bold text-white">$12,000</p>
            </div>
          </div>
        </GlassCard>

        <GlassCard className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
              <Award className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <p className="text-xs text-slate-300">Avg Scholarship</p>
              <p className="text-lg font-bold text-white">$15,000</p>
            </div>
          </div>
        </GlassCard>

        <GlassCard className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-purple-400" />
            </div>
            <div>
              <p className="text-xs text-slate-300">Trade ROI</p>
              <p className="text-lg font-bold text-emerald-400">+320%</p>
            </div>
          </div>
        </GlassCard>
      </div>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="w-full bg-slate-800/50 p-1 mb-6 grid grid-cols-4">
          <TabsTrigger value="budget" className="data-[state=active]:bg-blue-600">
            <Calculator className="w-4 h-4 mr-2" /> Budget Planner
          </TabsTrigger>
          <TabsTrigger value="compare" className="data-[state=active]:bg-blue-600">
            <BarChart3 className="w-4 h-4 mr-2" /> Path Comparison
          </TabsTrigger>
          <TabsTrigger value="resources" className="data-[state=active]:bg-blue-600">
            <BookOpen className="w-4 h-4 mr-2" /> Resources
          </TabsTrigger>
          <TabsTrigger value="scholarships" className="data-[state=active]:bg-blue-600">
            <Award className="w-4 h-4 mr-2" /> Scholarships
          </TabsTrigger>
        </TabsList>

        <TabsContent value="budget">
          <GlassCard className="p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                <Calculator className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-white">Family Budget Planner</h2>
                <p className="text-slate-300 text-sm">Calculate how much you need to save and explore options</p>
              </div>
            </div>
            <BudgetCalculator onNeedMoreIncome={handleNeedMoreIncome} />
          </GlassCard>
        </TabsContent>

        <TabsContent value="compare">
          <GlassCard className="p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
                <BarChart3 className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-white">Lifetime Path Cost Simulator</h2>
                <p className="text-slate-300 text-sm">Compare costs, ROI, and break-even across all paths</p>
              </div>
            </div>
            <PathComparisonTool />
          </GlassCard>
        </TabsContent>

        <TabsContent value="resources">
          <GlassCard className="p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                <BookOpen className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-white">Financial Resources Hub</h2>
                <p className="text-slate-300 text-sm">Savings, loans, aid, and scholarships all in one place</p>
              </div>
            </div>
            <FinancialResourcesPanel />
          </GlassCard>
        </TabsContent>

        <TabsContent value="scholarships">
          <GlassCard className="p-6">
            <ScholarshipTracker />
          </GlassCard>
        </TabsContent>
      </Tabs>

      {/* Career Professional Upsell Banner */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="mt-8"
      >
        <div className="p-6 rounded-2xl bg-gradient-to-r from-indigo-900/50 to-purple-900/50 border border-indigo-500/30">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-indigo-500/20 rounded-2xl flex items-center justify-center">
                <Briefcase className="w-7 h-7 text-indigo-400" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-white mb-1">Need to Increase Your Income?</h3>
                <p className="text-slate-300">
                  Unlock our Career Professional platform to explore side hustles, career advancement, and income-boosting strategies.
                </p>
              </div>
            </div>
            <Button 
              onClick={() => navigate('/app/careers')}
              className="bg-indigo-600 hover:bg-indigo-700 px-6"
            >
              <Rocket className="w-4 h-4 mr-2" />
              Explore Career Options
            </Button>
          </div>
        </div>
      </motion.div>
    </AppLayout>
  );
}
